import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/** `?dm=<patientId>` opens the DM control for that patient; `&dmStep=teeth` its second step. */
export const DM_CONTROL_PARAM = 'dm';
export const DM_CONTROL_STEP_PARAM = 'dmStep';
const TEETH_STEP = 'teeth';

export type DmControlStep = 'main' | 'teeth';

// Opening and changing step push a history entry, so browser Back walks the modal back; closing
// replaces it, like the work queue's appointments overlay.
export function useDmControlRoute(patientId: string) {
    const [searchParams, setSearchParams] = useSearchParams();
    const isOpen = searchParams.get(DM_CONTROL_PARAM) === patientId;
    const step: DmControlStep | null = !isOpen
        ? null
        : searchParams.get(DM_CONTROL_STEP_PARAM) === TEETH_STEP
          ? 'teeth'
          : 'main';

    const go = useCallback(
        (next: DmControlStep | null, replace: boolean) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    if (next === null) params.delete(DM_CONTROL_PARAM);
                    else params.set(DM_CONTROL_PARAM, patientId);
                    if (next === 'teeth') params.set(DM_CONTROL_STEP_PARAM, TEETH_STEP);
                    else params.delete(DM_CONTROL_STEP_PARAM);
                    return params;
                },
                { replace }
            );
        },
        [patientId, setSearchParams]
    );

    return {
        step,
        open: useCallback(() => go('main', false), [go]),
        openTeeth: useCallback(() => go('teeth', false), [go]),
        leaveTeeth: useCallback(() => go('main', true), [go]),
        close: useCallback(() => go(null, true), [go])
    };
}
